import React, { useState, useEffect } from 'react'
import { Form } from 'react-bootstrap'
import { CountryDropdown } from 'react-country-region-selector'
import { FormikErrors, FormikTouched } from 'formik'
import {
  getProvinces,
  getRegion,
  getMunicipalities,
  getBarangays
} from '../../../../config/regions/philippines'
import { toTitleCase } from '../../../../utility/stringUtils'

interface PhilippinesAddressFieldsProps {
  values: {
    address1: string
    address2: string
    city: string
    region: string
    country: string
    zipCode: string
  }
  errors: FormikErrors<{
    address1: string
    address2: string
    city: string
    region: string
    country: string
    zipCode: string
  }>
  handleChange: {
    (e: React.ChangeEvent<any>): void
    <T = string | React.ChangeEvent<any>>(
      field: T
    ): T extends React.ChangeEvent<any>
      ? void
      : (e: string | React.ChangeEvent<any>) => void
  }
  handleBlur: {
    (e: React.FocusEvent<any>): void
    <T = any>(fieldOrEvent: T): T extends string ? (e: any) => void : void
  }
  touched: FormikTouched<{
    address1: string
    address2: string
    city: string
    region: string
    country: string
    zipCode: string
  }>
}

const PhilippinesAddressFields: React.FC<PhilippinesAddressFieldsProps> = ({
  values,
  errors,
  handleChange,
  handleBlur,
  touched
}) => {
  const [selectRegion, setSelectRegion] = useState('')
  const [selectMunicipality, setSelectMunicipality] = useState('')

  const getProvinceList = () => {
    return getProvinces()
  }
  const getMunicipalityList = () => {
    return getMunicipalities(
      getRegion(selectRegion.toUpperCase())!,
      selectRegion.toUpperCase()
    )
  }
  const getBarangayList = () => {
    return getBarangays(
      getRegion(selectRegion.toUpperCase())!,
      selectRegion.toUpperCase(),
      selectMunicipality.toUpperCase()
    )
  }

  useEffect(() => {}, [selectRegion, selectMunicipality])

  return (
    <div data-testid="PhilippinesAddressFieldFC">
      <Form.Group>
        <Form.Label htmlFor="recipient-country">Country</Form.Label>
        <Form.Control
          id="recipient-country"
          as={CountryDropdown}
          name="country"
          value={values.country}
          data-testid="countryOption"
          disabled
        />
      </Form.Group>

      <Form.Group>
        <Form.Label htmlFor="recipient-zipCode">Postal code</Form.Label>
        <Form.Control
          id="recipient-zipCode"
          type="text"
          name="zipCode"
          placeholder="type postal code"
          value={values.zipCode}
          onChange={handleChange}
          onBlur={handleBlur}
          isInvalid={touched.zipCode && !!errors.zipCode}
        />
        <Form.Control.Feedback type="invalid">
          {errors.zipCode}
        </Form.Control.Feedback>
      </Form.Group>

      <Form.Group>
        <Form.Label htmlFor="recipient-province" className="pt-2 mt-1">
          Province
        </Form.Label>

        <Form.Control
          as="select"
          id="recipient-province"
          value={values.region}
          onChange={e => {
            if (selectRegion) {
              setSelectRegion('')
              setSelectMunicipality('')
            }
            handleChange(e)
            setSelectRegion(e.currentTarget.value)
          }}
          name="region"
        >
          <option value={values.region}>
            {values.region || 'Select province..'}
          </option>
          {getProvinceList().map(item => {
            return (
              <option key={item} value={toTitleCase(item)}>
                {toTitleCase(item)}
              </option>
            )
          })}
        </Form.Control>
      </Form.Group>

      <Form.Group>
        <Form.Label htmlFor="recipient-city" data-testid="recipient-city">
          City/Municipality
        </Form.Label>
        <Form.Control
          as="select"
          id="recipient-city"
          value={values.city}
          onChange={e => {
            handleChange(e)
            setSelectMunicipality(e.currentTarget.value)
          }}
          name="city"
        >
          <option value={values.city}>
            {values.city || 'Select municipality or city..'}
          </option>
          {selectRegion &&
            getMunicipalityList().map(item => {
              return (
                <option key={item} value={toTitleCase(item)}>
                  {toTitleCase(item)}
                </option>
              )
            })}
        </Form.Control>
      </Form.Group>

      <Form.Group>
        <Form.Label htmlFor="recipient-barangay">Barangay</Form.Label>
        <Form.Control
          as="select"
          value={values.address2}
          id="recipient-barangay"
          onChange={e => {
            handleChange(e)
          }}
          name="address2"
        >
          <option value={values.address2}>
            {values.address2 || 'Select a barangay..'}
          </option>
          {selectMunicipality &&
            getBarangayList().map((item: string) => {
              return (
                <option key={item} value={toTitleCase(item)}>
                  {toTitleCase(item)}
                </option>
              )
            })}
        </Form.Control>
      </Form.Group>

      <Form.Group>
        <Form.Label htmlFor="recipient-address1">Detailed Address</Form.Label>
        <Form.Control
          id="recipient-address1"
          type="text"
          name="address1"
          placeholder="Unit/house #, building/apartment/subdivision, street"
          value={values.address1}
          onChange={handleChange}
          onBlur={handleBlur}
          isInvalid={touched.address1 && !!errors.address1}
        />
        <Form.Control.Feedback type="invalid">
          {errors.address1}
        </Form.Control.Feedback>
      </Form.Group>
    </div>
  )
}

export default PhilippinesAddressFields
